/**
 * MergeToastOverlay — tiny ephemeral pill toast rendered bottom-center.
 *
 * Unlike DiscoveryModalOverlay (a full modal for first-time discoveries),
 * this is a passive "you made a thing" nod — shown when a merge produces an
 * element the player has already discovered before. No backdrop, no
 * interaction, auto-dismisses in ~1.4s.
 *
 * The overlay is intentionally static-function based (`MergeToast.show(...)`)
 * rather than extending DomOverlay because it has no events and multiple
 * toasts should stack vertically. Managing N open-state lifecycles through
 * the DomOverlay event bus is overkill for a write-only pill.
 */
export interface MergeToastData {
  label: string;      // e.g. "Gem · Fire"
  familyColor: number; // 0xRRGGBB
}

export const MergeToast = {
  show(data: MergeToastData): void {
    const host = document.body;
    if (!document.getElementById('merge-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'merge-toast-styles';
      style.textContent = MERGE_TOAST_STYLES;
      document.head.appendChild(style);
    }

    const stack = ensureStack();
    const toast = document.createElement('div');
    toast.className = 'merge-toast';
    toast.style.setProperty('--merge-color', `#${data.familyColor.toString(16).padStart(6, '0')}`);

    const dot = document.createElement('span');
    dot.className = 'merge-toast__dot';
    toast.appendChild(dot);

    const label = document.createElement('span');
    label.className = 'merge-toast__label';
    label.textContent = data.label;
    toast.appendChild(label);

    stack.appendChild(toast);
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 280);
    }, 1400);

    // Cap the stack so rapid cascade chains don't pile up infinitely.
    while (stack.childElementCount > 4) {
      stack.firstElementChild?.remove();
    }

    // Silence unused host lint — host reference kept for clarity of intent.
    void host;
  },
};

function ensureStack(): HTMLDivElement {
  let stack = document.getElementById('merge-toast-stack') as HTMLDivElement | null;
  if (!stack) {
    stack = document.createElement('div');
    stack.id = 'merge-toast-stack';
    stack.className = 'merge-toast-stack';
    document.body.appendChild(stack);
  }
  return stack;
}

const MERGE_TOAST_STYLES = `
.merge-toast-stack {
  position: fixed;
  left: 50%;
  bottom: calc(120px + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  display: flex; flex-direction: column-reverse; gap: 6px;
  z-index: 950;
  pointer-events: none;
}
.merge-toast {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  background: rgba(13, 14, 20, 0.92);
  border: 1px solid color-mix(in srgb, var(--merge-color, #00F0FF) 45%, transparent);
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-primary);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 220ms var(--ease-snap), transform 280ms var(--ease-spring);
}
.merge-toast.is-visible {
  opacity: 1;
  transform: translateY(0);
}
.merge-toast__dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--merge-color, var(--accent-primary));
  box-shadow: 0 0 6px var(--merge-color, var(--accent-primary));
}
.merge-toast__label { line-height: 1; }
@media (prefers-reduced-motion: reduce) {
  .merge-toast { transition: opacity 120ms linear; transform: none !important; }
}
`;
